import type {
  AspectRatio,
  BlockMedia,
  IImageComponentProps,
  IImageCrop,
  ImageAspectRatio,
} from "@newxsystems/types";
import { getComponent } from "../../../../client";
import type { IBlockImageProps } from "./BlockImage.props";

type RegisteredImageProps = Omit<
  IImageComponentProps,
  "alt" | "caption" | "sizes" | "src" | "srcSet"
> & {
  alt: BlockMedia["alternateText"];
  caption?: string;
  height?: number;
  sizes?: string;
  src: BlockMedia["defaultSrc"];
  srcSet?: string;
  width?: number;
};

type FixedImagePresentation = {
  aspectRatio: ImageAspectRatio;
  crop: IImageCrop;
};

const fixedImagePresentations: Record<AspectRatio, FixedImagePresentation> = {
  "1:1": { aspectRatio: "square", crop: { aspectRatio: "square" } },
  "4:3": { aspectRatio: "4/3", crop: { aspectRatio: "4/3" } },
  "3:2": { aspectRatio: "3/2", crop: { aspectRatio: "3/2" } },
  "16:9": { aspectRatio: "16/9", crop: { aspectRatio: "16/9" } },
  "21:9": { aspectRatio: "5/2", crop: { aspectRatio: "5/2" } },
  "3:4": { aspectRatio: "3/4", crop: { aspectRatio: "3/4" } },
  "2:3": { aspectRatio: "2/3", crop: { aspectRatio: "2/3" } },
};

export const BlockImage = ({
  image,
  effectiveAspectRatio,
  className,
  aspectRatioClassName,
  imageClassName,
  loading,
  priority,
}: IBlockImageProps) => {
  const Image = getComponent<RegisteredImageProps>("Image");

  if (!Image) {
    return null;
  }

  const media = image.image;
  const presentation =
    effectiveAspectRatio === "unrestricted"
      ? ({ aspectRatio: "infographic" } as const)
      : fixedImagePresentations[effectiveAspectRatio];

  return (
    <Image
      {...presentation}
      alt={media.alternateText}
      aspectRatioClassName={aspectRatioClassName}
      className={className}
      height={media.height ?? undefined}
      imageClassName={imageClassName}
      loading={loading}
      priority={priority}
      sizes={media.sizes ?? undefined}
      src={media.defaultSrc}
      srcSet={media.srcSet ?? undefined}
      width={media.width ?? undefined}
      {...(media.caption != null ? { caption: media.caption } : {})}
    />
  );
};

BlockImage.displayName = "Shared::BlockImage";
